import { roleAtLeast } from '@inou/shared';
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';

import { AnnouncementBanner } from '@/components/layout/AnnouncementBanner';
import { MobileTopBar } from '@/components/layout/MobileTopBar';
import { Sidebar } from '@/components/layout/Sidebar';
import type { Announcement, Me, OnboardingStatus } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { ViewerProvider } from '@/lib/viewer';

const ROLE_LABEL: Record<string, string> = {
  admin: '管理者',
  member: 'メンバー',
  mcp: 'MCP Connect',
};

export function AppLayout({ me, onSignedOut }: { me: Me; onSignedOut: () => void }) {
  const { data } = useApi<{ announcements: Announcement[] }>('/api/reports/announcements');
  /*
   * 「はじめに」を出すかは実データが決める (そのテナントにまだ工数が無いか)。
   * mcp では叩かない — /api/onboarding は 403 だし、あの人たちの home は
   * MCP接続の画面そのもので、案内としてはそれで足りている。
   */
  const { data: onboarding } = useApi<OnboardingStatus>(
    roleAtLeast(me.user.role, 'member') ? '/api/onboarding' : null,
  );

  /*
   * 768px 未満ではサイドバーをドロワーにする。開閉はここが持つ
   * (トップバーのボタン・ナビの遷移・バックドロップ・Escape の 4 つが触るため)。
   * 768px 以上ではサイドバーが常に出るので、この状態は誰も見ない。
   */
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // 遷移したら閉じる。開いたままだと、着いた先がドロワーに隠れる。
  // NavLink の onClick でも閉じているが、画面の中のリンクから飛んだときはここが効く。
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname は依存ではなく発火の合図そのもの。外すと初回だけ走って遷移で閉じなくなる
  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  // ロールは画面間のリンクの出し分けに要る (表の行の奥まで届かせるため context)
  return (
    <ViewerProvider role={me.user.role}>
      <div className="flex min-h-screen bg-surface">
        <Sidebar
          userName={me.user.name}
          userRoleLabel={ROLE_LABEL[me.user.role] ?? me.user.role}
          role={me.user.role}
          showStart={onboarding?.active === true}
          onSignedOut={onSignedOut}
          open={menuOpen}
          onNavigate={() => setMenuOpen(false)}
        />
        {menuOpen ? (
          /* 重ねたドロワーの外側。押すと閉じる。768px 以上には存在しない。 */
          <button
            type="button"
            aria-label="メニューを閉じる"
            data-testid="sidebar-backdrop"
            onClick={() => setMenuOpen(false)}
            className="fixed inset-0 z-30 bg-ink/30 md:hidden"
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <MobileTopBar open={menuOpen} onToggle={() => setMenuOpen((value) => !value)} />
          <AnnouncementBanner announcements={data?.announcements ?? []} />
          <Outlet />
        </div>
      </div>
    </ViewerProvider>
  );
}
